import { ReactNode } from 'react';
import { TermsDocumentPublic } from '../../../types';

interface TermsFinalReviewProps {
  documents: TermsDocumentPublic[];
  reviewedAt: Map<string, string>;
  isArabic: boolean;
  isSubmitting: boolean;
  canSubmit: boolean;
  error: string | null;
  downloadingDocId: string | null;
  onViewAgain: (docId: string) => void;
  onDownload: (doc: TermsDocumentPublic) => void;
  onSubmit: () => void;
  /** Spec 071 — the signature block (or its load error), rendered between the list and the error. */
  signatureSlot?: ReactNode;
  labels: {
    eyebrow: string;
    title: string;
    subtitle: string;
    rowAgreed: string;
    viewAgain: string;
    download: string;
    downloadInProgress: string;
    submit: string;
    submitting: string;
  };
}

const formatTime = (iso: string, isArabic: boolean): string => {
  try {
    return new Date(iso).toLocaleTimeString(isArabic ? 'ar' : 'en', {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
};

export default function TermsFinalReview({
  documents,
  reviewedAt,
  isArabic,
  isSubmitting,
  canSubmit,
  error,
  downloadingDocId,
  onViewAgain,
  onDownload,
  onSubmit,
  signatureSlot,
  labels,
}: TermsFinalReviewProps) {
  return (
    <div className="terms-summary-content" key="summary">
      <span className="terms-summary-eyebrow">{labels.eyebrow}</span>
      <div className="terms-summary-icon" aria-hidden="true">
        <svg
          width="40"
          height="40"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M9 12l2 2 4-4" />
        </svg>
      </div>
      <h3 className="terms-summary-title">{labels.title}</h3>
      <p className="terms-summary-subtitle">{labels.subtitle}</p>

      <ul className="terms-summary-list">
        {documents.map((doc) => {
          const title = isArabic && doc.titleAr ? doc.titleAr : doc.title;
          const ts = reviewedAt.get(doc.id);
          const isThisDownloading = downloadingDocId === doc.id;
          return (
            <li key={doc.id} className="terms-summary-row">
              <span className="terms-summary-check" aria-hidden="true">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#fff"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              </span>
              <div className="terms-summary-row-text">
                <strong>{title}</strong>
                {ts && (
                  <em>
                    {labels.rowAgreed} · {formatTime(ts, isArabic)}
                  </em>
                )}
              </div>
              <div className="terms-summary-row-actions">
                <button
                  type="button"
                  className="terms-summary-download"
                  onClick={() => onDownload(doc)}
                  disabled={isThisDownloading}
                  aria-label={labels.download}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  {isThisDownloading ? labels.downloadInProgress : labels.download}
                </button>
                <button
                  type="button"
                  className="terms-summary-edit"
                  onClick={() => onViewAgain(doc.id)}
                >
                  {labels.viewAgain}
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      {signatureSlot}

      {error && (
        <div className="error-message" role="alert" style={{ marginTop: '1rem' }}>
          {error}
        </div>
      )}

      <button
        type="button"
        className="terms-submit-final"
        disabled={!canSubmit}
        onClick={onSubmit}
      >
        {isSubmitting ? labels.submitting : labels.submit}
      </button>
    </div>
  );
}
